/**
 * @typedef {Object} Integration
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} description - Description
 * @property {string} icon - Icon/emoji
 * @property {string} [provider] - Provider name (e.g., "ZOHO")
 * @property {string} [product] - Product name (e.g., "BOOKS", "PROJECTS")
 * @property {string} status - Status ("connected", "not connected", "error", "pending")
 * @property {string} [integrationName] - Backend integration identifier
 * @property {boolean} [supportsDisconnect] - Whether disconnect is supported
 * @property {Object} [metadata] - Product-specific metadata
 */

/**
 * Integration product types
 */
export const IntegrationProduct = {
  BOOKS: "BOOKS",
  PROJECTS: "PROJECTS",
};

/**
 * Integration providers
 */
export const IntegrationProvider = {
  ZOHO: "ZOHO",
  QUICKBOOKS: "QUICKBOOKS",
  XERO: "XERO",
  ODOO: "ODOO",
  HOLDED: "HOLDED",
};

/**
 * Integration status types
 */
export const IntegrationStatus = {
  CONNECTED: "connected",
  NOT_CONNECTED: "not connected",
  ERROR: "error",
  PENDING: "pending",
};

/**
 * Map backend integration names to frontend identifiers
 * @param {string} backendIntegrationName - Backend format (e.g., "INTEGRATION#ZOHO#BOOKS")
 * @returns {Object} { provider, product }
 */
export function parseBackendIntegrationName(backendIntegrationName) {
  const parts = backendIntegrationName.split("#");
  if (parts.length >= 3) {
    return {
      provider: parts[1],
      product: parts[2],
    };
  }
  if (parts.length >= 2) {
    return {
      provider: parts[1],
      product: null,
    };
  }
  return null;
}

/**
 * Create backend integration name from provider and product
 * @param {string} provider - Provider name
 * @param {string} [product] - Product name (optional)
 * @returns {string}
 */
export function createBackendIntegrationName(provider, product) {
  if (product) {
    return `INTEGRATION#${provider}#${product}`;
  }
  return `INTEGRATION#${provider}`;
}
